/* eslint-disable no-console */
// @aiContributed-2025-11-07
import React, { useEffect, useMemo } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import { PageReady } from '@bingads-webui/page-ready';
import { withRouter } from '@bingads-webui-campaign-react/router';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import type {
  ElementInfo,
  ExtendedNavigator,
  ExtendedPerformance,
  FirstContentfulPaintEntry,
  InteractionEntry,
  LargestContentfulPaintEntry,
  LayoutShiftEntry,
  MetricLog,
  MetricName,
  MetricsElement,
  NavigationEntry,
  WebMetricsLoggerFields,
  WebMetricsProps,
  WebPerformanceEntry,
} from './types';

const DEFAULT_DURATION_THRESHOLD = 40;

const Metrics: Record<MetricName, MetricName> = {
  LCP: 'LCP', // Largest Contentful Paint
  CLS: 'CLS', // Cumulative Layout Shift
  INP: 'INP', // Interaction to Next Paint
  FCP: 'FCP', // First Contentful Paint
  CLS_ROUTE: 'CLS_ROUTE', // Cumulative Layout Shift (Route Change)
  DEVICE_INFO: 'DEVICE_INFO', // Device Information
  NAVIGATION_INFO: 'NAVIGATION_INFO', // Navigation Timing Information
};

const LogThresholds: Partial<Record<MetricName, number>> = {
  [Metrics.CLS]: 0.0002,
};

class WebMetricsLogger {
  constructor({
    instrumentation
  }: Pick<WebMetricsProps, 'instrumentation'>) {
    this.options = {
      enableConsoleOutput: window.location.hostname.includes('app1'),
      instrumentation,
    };

    this.observer = null;
    this.isInitialized = false;
    this.route = null;

    // Bind methods to preserve context
    this.handlePerformanceEntry = this.handlePerformanceEntry.bind(this);
  }

  init() {
    if (this.isInitialized) {
      return;
    }

    this.isInitialized = true;

    if (typeof window === 'undefined' || !window.PerformanceObserver) {
      this.logError('PerformanceObserver not supported');
      return;
    }

    this.metrics = new Map();
    this.scenario = (this.options.instrumentation && _.isFunction(this.options.instrumentation.createScenario) && this.options.instrumentation.createScenario!({ scenarioName: 'web-metrics' })) as WebMetricsLoggerFields['scenario'];
    this.activity = (this.scenario && _.isFunction(this.scenario.createActivity) && this.scenario.createActivity('web-metrics-activity')) as WebMetricsLoggerFields['activity'];
    this.initObserver();
  }

  updateRoute(pathname: string) {
    this.route = pathname;
    if (this.metrics) {
      this.metrics.set(Metrics.CLS_ROUTE, 0);
    }
  }

  initObserver() {
    try {
      this.observer = new PerformanceObserver(this.handlePerformanceEntry);

      const entryTypes = [
        'largest-contentful-paint',
        'layout-shift',
        'paint',
        'event',
        'navigation',
      ];

      const supportedTypes = entryTypes.filter((type) => {
        try {
          return PerformanceObserver.supportedEntryTypes && PerformanceObserver.supportedEntryTypes.includes(type);
        } catch (e) {
          return false;
        }
      });

      if (supportedTypes.length > 0) {
        this.observer.observe({ entryTypes: supportedTypes });
      }

      this.logMetric({
        name: Metrics.DEVICE_INFO,
        entry: {
          connectionType: this.getConnectionType(),
          deviceMemory: this.getDeviceMemory(),
          navigationTiming: this.getNavigationTiming(),
        },
      });
    } catch (error) {
      this.logError('Performance observer initialization failed', error);
    }
  }

  handlePerformanceEntry(list: PerformanceObserverEntryList) {
    if (document.visibilityState !== 'visible') {
      return;
    }

    const entries = list.getEntries() as WebPerformanceEntry[];

    entries.forEach((entry) => {
      switch (entry.entryType) {
        case 'largest-contentful-paint':
          this.handleLCP(entry);
          break;
        case 'layout-shift':
          this.handleCLS(entry);
          break;
        case 'paint':
          this.handleFCP(entry);
          break;
        case 'event':
          this.handleINP(entry);
          break;
        case 'navigation':
          this.handleNavigation(entry);
          break;
        default:
          // Ignore unknown entry types
          break;
      }
    });
  }

  getFirstClassNames(className: unknown, count = 3) {
    if (!className) return null;
    const classList = String(className).trim().split(/\s+/);
    if (classList.length === 0) {
      return null;
    }
    return classList.slice(0, count).join(' ');
  }

  getElementInfo(node: MetricsElement | null | undefined): ElementInfo | null {
    if (!node) return null;

    const info: ElementInfo = {
      ...(node.id && { id: String(node.id) }),
      ...(node.className && { className: String(this.getFirstClassNames(node.className)) }),
      ...(node.tagName && { tagName: node.tagName }),
    };

    try {
      let childContent;

      if (node.firstElementChild && (node.firstElementChild.className || node.firstElementChild.id)) {
        childContent = {
          ...(node.firstElementChild.tagName && { tagName: node.firstElementChild.tagName }),
          ...(node.firstElementChild.id && { id: String(node.firstElementChild.id) }),
          ...(node.firstElementChild.className && { className: String(this.getFirstClassNames(node.firstElementChild.className)) }),
        };
      } else if (node.firstChild && node.firstChild.nodeType === Node.TEXT_NODE) {
        const textContent = node.firstChild.textContent?.trim();
        childContent = textContent && textContent.length > 100
          ? `${textContent.substring(0, 97)}...`
          : textContent;
      } else if (node.textContent) {
        const textContent = node.textContent.trim();
        childContent = textContent && textContent.length > 100
          ? `${textContent.substring(0, 97)}...`
          : textContent;
      }

      if (childContent) {
        info.childContent = childContent;
      }
    } catch (error) {
      this.logError('Failed to get child content', error);
    }

    try {
      let parentElement;

      if (node.parentElement && (node.parentElement.className || node.parentElement.id)) {
        parentElement = {
          ...(node.parentElement.tagName && { tagName: node.parentElement.tagName }),
          ...(node.parentElement.id && { id: String(node.parentElement.id) }),
          ...(node.parentElement.className && { className: String(this.getFirstClassNames(node.parentElement.className)) }),
        };
        info.parentElement = parentElement;
      }
    } catch (error) {
      this.logError('Failed to get parent element', error);
    }

    return info;
  }

  handleLCP(entry: LargestContentfulPaintEntry) {
    this.updateMetric({
      name: Metrics.LCP,
      value: entry.startTime,
      entry: {
        element: this.getElementInfo(entry.element),
        id: entry.id || null,
        size: entry.size || null,
        now: _.isFunction(performance.now) ? performance.now() : 0,
        activeTime: _.isFunction((performance as ExtendedPerformance).activeTime) ? (performance as ExtendedPerformance).activeTime!() : 0,
      },
    });
  }

  handleCLS(entry: LayoutShiftEntry) {
    if (entry.hadRecentInput) {
      return;
    }

    this.updateMetric({
      name: Metrics.CLS_ROUTE,
      value: (this.metrics.get(Metrics.CLS_ROUTE) || 0) + entry.value,
      entry: { route: this.route },
    });

    const clsValue = (this.metrics.get(Metrics.CLS) || 0) + entry.value;
    const sources = entry && entry.sources ? entry.sources.map(source => ({
      ...this.getElementInfo(source.node),
      previousRect: source.previousRect,
      currentRect: source.currentRect
    })) : undefined;

    this.updateMetric({
      name: Metrics.CLS,
      value: clsValue,
      entry: {
        sources,
        [Metrics.CLS_ROUTE]: this.metrics.get(Metrics.CLS_ROUTE),
        route: this.route,
        value: entry.value,
      },
    });
  }

  handleFCP(entry: FirstContentfulPaintEntry) {
    if (entry.name === 'first-contentful-paint') {
      this.updateMetric({ name: Metrics.FCP, value: entry.startTime, entry });
    }
  }

  handleINP(entry: InteractionEntry) {
    if (!entry.interactionId) {
      return;
    }

    if (entry.duration <= DEFAULT_DURATION_THRESHOLD) {
      return;
    }

    this.updateMetric({
      name: Metrics.INP,
      value: Math.max(this.metrics.get(Metrics.INP) || 0, entry.duration),
      entry: {
        name: entry.name,
        entryType: entry.entryType,
        value: entry.duration,
        target: this.getElementInfo(entry.target),
      },
    });
  }

  handleNavigation(entry: NavigationEntry) {
    this.logMetric({
      name: Metrics.NAVIGATION_INFO,
      entry: {
        type: entry.type,
        redirectCount: entry.redirectCount,
        transferSize: entry.transferSize,
        encodedBodySize: entry.encodedBodySize,
        decodedBodySize: entry.decodedBodySize,
        domContentLoadedEventStart: entry.domContentLoadedEventStart,
        domContentLoadedEventEnd: entry.domContentLoadedEventEnd,
        loadEventStart: entry.loadEventStart,
        loadEventEnd: entry.loadEventEnd,
        route: this.route,
        referrer: document.referrer,
      },
    });
  }

  logMetric({
    name,
    value,
    entry = {}
  }: MetricLog) {
    if (name === Metrics.CLS_ROUTE) {
      // Skip logging CLS_ROUTE as it is logged with CLS
      return;
    }

    if (LogThresholds[name] && (entry as { value?: number }).value! < LogThresholds[name]!) {
      // Skip logging metrics below threshold
      return;
    }

    const metricData = {
      name,
      entry,
      ...(value !== undefined ? { value } : {}),
    };

    if (this.options.enableConsoleOutput) {
      console.log(`WebMetric logged: ${name}, ${value}`, metricData);
    }
    if (this.activity) {
      PageReady.afterPageReady().then(() => {
        this.activity.trace(metricData);
      });
    }
  }

  updateMetric({
    name,
    value,
    entry
  }: Required<MetricLog>) {
    this.metrics.set(name, value);

    this.logMetric({ name, value, entry });
  }

  getConnectionType() {
    const browserNavigator = navigator as ExtendedNavigator;
    if (browserNavigator.connection) {
      return {
        effectiveType: browserNavigator.connection.effectiveType,
        downlink: browserNavigator.connection.downlink,
        rtt: browserNavigator.connection.rtt,
      };
    }
    return null;
  }

  getDeviceMemory() {
    return (navigator as ExtendedNavigator).deviceMemory || null;
  }

  getNavigationTiming() {
    try {
      if (performance.getEntriesByType) {
        const navigationEntries = performance.getEntriesByType('navigation');
        if (navigationEntries && navigationEntries.length > 0) {
          const navEntry = navigationEntries[0] as NavigationEntry;
          return {
            type: navEntry.type,
            redirectCount: navEntry.redirectCount,
            transferSize: navEntry.transferSize,
            encodedBodySize: navEntry.encodedBodySize,
            decodedBodySize: navEntry.decodedBodySize,
          };
        }
      }
      return null;
    } catch (error) {
      this.logError('Failed to get navigation timing', error);
      return null;
    }
  }

  logError(message: string, error?: unknown) {
    if (this.options.enableConsoleOutput) {
      console.error(`WebMetrics: ${message}`, error);
    }
    PageReady.afterPageReady().then(() => {
      this.activity.trace(`WebMetric logged: ${message} Error: ${JSON.stringify(error)}`);
    });
  }

  destroy() {
    if (this.observer) {
      try {
        this.observer.disconnect();
      } catch (error) {
        this.logError('Failed to disconnect observer', error);
      }
      this.observer = null;
    }

    if (this.metrics) {
      this.metrics.clear();
      this.metrics = null;
    }
    this.isInitialized = false;
  }
}

interface WebMetricsLogger extends WebMetricsLoggerFields {}

const MetricLogger = ({
  instrumentation,
  location: { pathname }
}: WebMetricsProps): null => {
  const webMetrics = useMemo(() => new WebMetricsLogger({
    instrumentation,
  }), [instrumentation]);

  useEffect(() => {
    webMetrics.init();

    return () => {
      webMetrics.destroy();
    };
  }, [webMetrics]);

  useEffect(() => {
    if (pathname) {
      webMetrics.updateRoute(pathname);
    }
  }, [pathname, webMetrics]);

  return null;
};

MetricLogger.propTypes = {
  instrumentation: PropTypes.shape({
    createScenario: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
} as unknown as React.WeakValidationMap<WebMetricsProps>;

const loggerWithHOC = withRouter(withInstrumentation(MetricLogger)) as React.FunctionComponent<WebMetricsProps>;

export const WebMetrics = React.memo<WebMetricsProps>(loggerWithHOC, (prevProps, nextProps) =>
  prevProps.location.pathname === nextProps.location.pathname);
